import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Nav, Navbar, Table, NavDropdown } from 'react-bootstrap';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { groupsAPI } from '../services/api';
import CoordinatorChangePassword from '../components/CoordinatorChangePassword';
import CoordinatorOrdersList from './CoordinatorOrdersList';

// Dashboard Components
const DashboardHome = () => {
  const [stats, setStats] = useState({
    userCount: 0,
    orderCount: 0,
    totalSales: 0,
    campaigns: 0
  });
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get user from localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('User data not found');
        }
        
        const user = JSON.parse(userData);
        if (!user.group || !user.group._id) {
          throw new Error('Group information not found');
        }
        
        // Fetch group details
        const groupResponse = await groupsAPI.getGroupById(user.group._id);
        setGroup(groupResponse.data.data);
        
        // Fetch group stats
        const statsResponse = await groupsAPI.getGroupStats(user.group._id);
        setStats(statsResponse.data.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading statistics...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <h2 className="mb-4">Group Dashboard</h2>
      
      {group && (
        <Card className="mb-4">
          <Card.Body>
            <h3>{group.name}</h3>
            <p className="text-muted">{group.organizationName}</p>
            <p>Group Code: <strong>{group.code}</strong></p>
          </Card.Body>
        </Card>
      )}
      
      <Row>
        <Col md={4}>
          <Card className="stats-card mb-4">
            <div className="stats-value">{stats.userCount}</div>
            <div className="stats-label">Students with orders</div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stats-card mb-4">
            <div className="stats-value">{stats.orderCount}</div>
            <div className="stats-label">Orders</div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stats-card mb-4">
            <div className="stats-value">${stats.totalSales.toFixed(2)}</div>
            <div className="stats-label">Total Sales</div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Campaigns List
const CampaignsList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        // Get user from localStorage
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('User data not found');
        }
        
        const user = JSON.parse(userData);
        if (!user.group || !user.group._id) {
          throw new Error('Group information not found');
        }
        
        // Fetch campaigns for this group
        const response = await groupsAPI.getGroupCampaigns(user.group._id);
        setCampaigns(response.data.data);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  if (loading) {
    return <div className="loading-spinner">Loading campaigns...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      <h2 className="mb-4">Fundraising Campaigns</h2>
      
      {campaigns.length > 0 ? (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Description</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => (
              <tr key={campaign._id}>
                <td>{campaign.description || 'No description'}</td>
                <td>{new Date(campaign.startDate).toLocaleDateString()}</td>
                <td>{new Date(campaign.endDate).toLocaleDateString()}</td>
                <td>
                  {campaign.isActive ? (
                    campaign.isCurrentlyActive ? (
                      <span className="text-success">Active</span>
                    ) : (
                      <span className="text-warning">
                        {new Date() < new Date(campaign.startDate) ? 'Upcoming' : 'Ended'}
                      </span>
                    )
                  ) : (
                    <span className="text-danger">Inactive</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-center">No campaigns found for your group.</p>
      )}
    </div>
  );
};

// Main Dashboard Component
const CoordinatorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('user');
    
    // Redirect to login
    navigate('/coordinator/login');
  };

  const handleChangePasswordSuccess = () => {
    // Optional: Show a success message or perform any additional actions
    console.log('Password changed successfully');
  };

  return (
    <div>
      <Navbar bg="dark" variant="dark" expand="lg">
        <Container>
          <Navbar.Brand as={Link} to="/coordinator">Coordinator Portal</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link as={Link} to="/coordinator" active={location.pathname === '/coordinator'}>
                Dashboard
              </Nav.Link>
              <Nav.Link as={Link} to="/coordinator/campaigns" active={location.pathname.startsWith('/coordinator/campaigns')}>
                Campaigns
              </Nav.Link>
              <Nav.Link as={Link} to="/coordinator/orders" active={location.pathname.startsWith('/coordinator/orders')}>
                Orders
              </Nav.Link>
            </Nav>
            <Nav>
              {user && (
                <NavDropdown title={`${user.name}`} id="user-nav-dropdown" align="end">
                  <NavDropdown.Item onClick={() => setShowChangePassword(true)}>
                    Change Password
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="dashboard-container">
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/campaigns" element={<CampaignsList />} />
          <Route path="/orders" element={<CoordinatorOrdersList />} />
        </Routes>
      </Container>

      {/* Change Password Modal */}
      <CoordinatorChangePassword
        show={showChangePassword}
        onHide={() => setShowChangePassword(false)}
        onSuccess={handleChangePasswordSuccess}
      />
    </div>
  );
};

export default CoordinatorDashboard;
